import { inventoryWorkloadsTableKeys } from '../consts';
import { WORKLOAD_CATEGORIES, type WorkloadCategory } from '../_constants';

const MONITORING_CATEGORIES = new Set<WorkloadCategory>([
  WORKLOAD_CATEGORIES.COMPUTE,
  WORKLOAD_CATEGORIES.NETWORK,
  WORKLOAD_CATEGORIES.RELIABILITY,
  WORKLOAD_CATEGORIES.GPU_UTILIZATION,
]);

const WOOP_LIST_CATEGORIES = new Set<WorkloadCategory>([
  WORKLOAD_CATEGORIES.AUTOSCALING,
  WORKLOAD_CATEGORIES.SAVINGS,
]);

const CATEGORY_SEARCH_URL_KEYS: Partial<Record<WorkloadCategory, string>> = {
  [WORKLOAD_CATEGORIES.AUTOSCALING]: inventoryWorkloadsTableKeys.AUTOSCALING,
  [WORKLOAD_CATEGORIES.SAVINGS]: inventoryWorkloadsTableKeys.SAVINGS,
  [WORKLOAD_CATEGORIES.COMPUTE]: inventoryWorkloadsTableKeys.COMPUTE,
  [WORKLOAD_CATEGORIES.NETWORK]: inventoryWorkloadsTableKeys.NETWORK,
  [WORKLOAD_CATEGORIES.RELIABILITY]: inventoryWorkloadsTableKeys.RELIABILITY,
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]:
    inventoryWorkloadsTableKeys.GPU_UTILIZATION,
};

const MONITORING_SHARED_FILTER_KEYS = [
  'namespaces',
  'workloadNames',
  'workloadTypes',
  'labels',
] as const;

const WOOP_SHARED_FILTER_KEYS = [
  'namespace',
  'workloadName',
  'workloadId',
] as const;

const decodeSearchState = (
  urlKey: string,
  searchParams: URLSearchParams
): Record<string, unknown> => {
  const raw = searchParams.get(urlKey);

  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const pickSharedFilters = (
  state: Record<string, unknown>,
  keys: readonly string[]
) =>
  Object.fromEntries(
    keys
      .filter((key) => state[key] !== undefined && state[key] !== null)
      .map((key) => [key, state[key]])
  );

const mapMonitoringToWoopFilters = (
  monitoringState: Record<string, unknown>
): Record<string, unknown> => {
  const mapped: Record<string, unknown> = {};

  if (monitoringState.namespaces !== undefined) {
    mapped.namespace = monitoringState.namespaces;
  }

  if (monitoringState.workloadNames !== undefined) {
    mapped.workloadName = monitoringState.workloadNames;
  }

  return mapped;
};

const mapWoopToMonitoringFilters = (
  woopState: Record<string, unknown>
): Record<string, unknown> => {
  const mapped: Record<string, unknown> = {};

  if (woopState.namespace !== undefined) {
    mapped.namespaces = woopState.namespace;
  }

  if (woopState.workloadName !== undefined) {
    mapped.workloadNames = woopState.workloadName;
  }

  return mapped;
};

export const carryInventoryListFiltersAcrossCategories = (
  fromCategory: WorkloadCategory | undefined,
  toCategory: WorkloadCategory,
  searchParams: URLSearchParams
): URLSearchParams => {
  if (!fromCategory || fromCategory === toCategory) {
    return searchParams;
  }

  const fromUrlKey = CATEGORY_SEARCH_URL_KEYS[fromCategory];
  const toUrlKey = CATEGORY_SEARCH_URL_KEYS[toCategory];

  if (!fromUrlKey || !toUrlKey || fromUrlKey === toUrlKey) {
    return searchParams;
  }

  const fromState = decodeSearchState(fromUrlKey, searchParams);
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.delete(fromUrlKey);

  let sharedFilters: Record<string, unknown> = {};

  if (
    MONITORING_CATEGORIES.has(fromCategory) &&
    MONITORING_CATEGORIES.has(toCategory)
  ) {
    sharedFilters = pickSharedFilters(fromState, MONITORING_SHARED_FILTER_KEYS);
  } else if (
    WOOP_LIST_CATEGORIES.has(fromCategory) &&
    WOOP_LIST_CATEGORIES.has(toCategory)
  ) {
    sharedFilters = pickSharedFilters(fromState, WOOP_SHARED_FILTER_KEYS);
  } else if (
    MONITORING_CATEGORIES.has(fromCategory) &&
    WOOP_LIST_CATEGORIES.has(toCategory)
  ) {
    sharedFilters = mapMonitoringToWoopFilters(fromState);
  } else if (
    WOOP_LIST_CATEGORIES.has(fromCategory) &&
    MONITORING_CATEGORIES.has(toCategory)
  ) {
    sharedFilters = mapWoopToMonitoringFilters(fromState);
  }

  if (!Object.keys(sharedFilters).length) {
    return nextParams;
  }

  const toState = {
    ...decodeSearchState(toUrlKey, searchParams),
    ...sharedFilters,
  };

  nextParams.set(toUrlKey, encodeURIComponent(JSON.stringify(toState)));

  return nextParams;
};
